import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead, { breadcrumb } from './SEOHead';
import LandingFooter from './LandingFooter';
import './landing.css';

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';
const TEAL = '#0ea98b';

const PLANS = [
  {
    name: 'Starter', price: '99', desc: 'For small teams getting started with digital HR. Up to 25 employees.',
    included: ['GPS Attendance Tracking', 'Leave Management', 'Basic Payroll Processing', 'Employee Self-Service App', 'Email Support'],
    excluded: ['Expense Claims', 'Performance Appraisals', 'L&D Module'],
    cta: 'Get started free', style: 'outline',
  },
  {
    name: 'Growth', price: '179', desc: 'For growing companies that need the full HRMS suite. Up to 200 employees.',
    included: ['Everything in Starter', 'Expense Claims & Approval', 'Performance Appraisals (360°)', 'Learning & Development', 'Rewards & Recognition', 'Policy Acknowledgement', 'Asset Tracking', 'Priority Support + Onboarding'],
    excluded: [], cta: 'Book a free Demo', style: 'primary', featured: true,
  },
  {
    name: 'Enterprise', price: null, desc: 'For large organisations needing custom workflows, integrations and dedicated support.',
    included: ['Everything in Growth', 'Custom Workflows & Roles', 'API & HRMS Integrations', 'SSO / SAML', 'Dedicated Success Manager', 'SLA-backed Uptime', 'Custom Data Retention', 'On-site Training Available'],
    excluded: [], cta: 'Talk to sales', style: 'ghost',
  },
];

const FAQS = [
  { q: 'Is there a free trial?', a: 'Yes — all plans include a 30-day free trial with full access to every feature. To start, book a free demo and we\'ll set up your trial account on the call, usually the same day. No credit card required. You can upgrade, downgrade or cancel at any time during or after the trial.' },
  { q: 'How is pricing calculated?', a: 'Pricing is per active employee per month. You only pay for employees currently active in the system — deactivated employees are never counted. Bills are generated on the 1st of each month.' },
  { q: 'Is DigiHR compliant with Indian labour laws?', a: "Yes. DigiHR is built for Indian compliance. The payroll engine handles PF, ESI, PT, TDS, and gratuity calculations. We update the system whenever statutory rates or rules change — you don't have to do anything." },
  { q: 'Can I migrate from another HRMS?', a: 'Absolutely. Our onboarding team handles data migration from any spreadsheet or HRMS. The Growth and Enterprise plans include a dedicated onboarding specialist who completes migration in under 7 working days.' },
  { q: 'What kind of support is available?', a: 'Starter plans get email support (response within 24 hours). Growth plans get priority email + chat support (response within 4 hours). Enterprise customers get a dedicated success manager and phone support with SLA guarantees.' },
];

// No Product schema here: Google treats Product as a merchant listing and demands
// image/shipping/returns. Plan prices live on the SoftwareApplication in index.html.
const pricingSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  breadcrumb('Pricing', '/pricing'),
];

const btnStyle = (variant) => {
  if (variant === 'primary') return { background: ACCENT, color: '#fff', border: 'none', boxShadow: '0 2px 8px rgba(26,95,255,.3)' };
  if (variant === 'outline') return { background: 'transparent', color: ACCENT, border: '1.5px solid #d0e0ff' };
  return { background: 'transparent', color: '#3a3f4a', border: '1px solid #e4e7ed' };
};

const Pricing = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <SEOHead
        title="HRMS Pricing India — From ₹99/Employee/Month | DigiHR"
        description="Transparent HRMS pricing from ₹99 per employee per month. GPS attendance, payroll, leave and performance in every plan. 30-day free trial, no credit card required."
        keywords="HRMS pricing India, HR software price India, payroll software cost India, attendance app price, affordable HRMS for small business"
        canonical="/pricing"
        schema={pricingSchema}
      />

      <section style={{ padding: '80px 5% 20px', background: 'linear-gradient(160deg,#f0f4ff,#fff)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Pricing</div>
            <h1 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 16 }}>Simple, honest pricing</h1>
            <p style={{ fontSize: 17, color: '#3a3f4a', lineHeight: 1.65, maxWidth: 560, margin: '0 auto' }}>Pay per employee. No hidden fees. Scale up or down as your team changes.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, alignItems: 'start', paddingBottom: 80 }} className="pricing-grid-resp">
            {PLANS.map(({ name, price, desc, included, excluded, cta, style, featured }) => (
              <div key={name} style={{
                background: featured ? '#0d1117' : '#fff',
                border: `1px solid ${featured ? '#0d1117' : '#e4e7ed'}`,
                borderRadius: 28, padding: 32, position: 'relative',
                color: featured ? '#fff' : 'inherit',
              }}>
                {featured && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: ACCENT, color: '#fff', fontSize: 11, fontWeight: 600, padding: '4px 14px', borderRadius: 100, whiteSpace: 'nowrap', letterSpacing: '.04em', textTransform: 'uppercase' }}>Most Popular</div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: featured ? 'rgba(255,255,255,.7)' : '#717888', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>{name}</div>
                <div style={{ ...serif, fontSize: price ? 42 : 32, color: featured ? '#fff' : '#0d1117', lineHeight: 1, marginBottom: 4 }}>
                  {price ? <>₹{price}<span style={{ fontSize: 14, fontFamily: '"DM Sans",system-ui,sans-serif', color: featured ? 'rgba(255,255,255,.6)' : '#717888' }}>/employee/mo</span></> : 'Custom'}
                </div>
                <div style={{ fontSize: 13, color: featured ? 'rgba(255,255,255,.7)' : '#717888', marginBottom: 24, paddingBottom: 24, borderBottom: `1px solid ${featured ? 'rgba(255,255,255,.1)' : '#e4e7ed'}` }}>{desc}</div>
                <ul style={{ listStyle: 'none', marginBottom: 28 }}>
                  {included.map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: featured ? 'rgba(255,255,255,.7)' : '#3a3f4a', padding: '6px 0' }}>
                      <span style={{ color: TEAL, fontWeight: 700, flexShrink: 0 }}>✓</span> {item}
                    </li>
                  ))}
                  {excluded.map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#3a3f4a', padding: '6px 0' }}>
                      <span style={{ color: '#717888', flexShrink: 0 }}>—</span> {item}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '9px 20px', borderRadius: 12, fontSize: 14, fontWeight: 500, textDecoration: 'none', ...btnStyle(style) }}>{cta}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '20px 5% 80px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>FAQ</div>
            <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117' }}>Frequently asked questions</h2>
          </div>
          <div style={{ maxWidth: 740, margin: '0 auto' }}>
            {FAQS.map(({ q, a }, i) => {
              const open = openFaq === i;
              return (
                <div key={q} style={{ border: '1px solid #e4e7ed', borderRadius: 20, marginBottom: 10, overflow: 'hidden' }}>
                  <div onClick={() => setOpenFaq(open ? null : i)}
                    style={{ padding: '18px 22px', fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none', background: '#fff' }}>
                    {q}
                    <span style={{ fontSize: 12, color: '#717888', transition: 'transform .3s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
                  </div>
                  {open && <div style={{ padding: '0 22px 18px', fontSize: 14, color: '#3a3f4a', lineHeight: 1.7 }}>{a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <LandingFooter />

      <style>{`@media(max-width:900px){ .pricing-grid-resp{ grid-template-columns:1fr !important; } }`}</style>
    </>
  );
};

export default Pricing;
